import { HStack, Heading, Stack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

type PageHeadingProps = {
  children: ReactNode;
  /** Вторая строка под заголовком: чем этот экран занимается. */
  description?: ReactNode;
  /** Действие экрана - кнопка справа от заголовка. */
  action?: ReactNode;
  /**
   * Уровень в структуре страницы. `h1` по умолчанию: у экрана один главный заголовок.
   * `h2` берут вложенные экраны - шаг мастера внутри страницы, панель внутри раздела.
   */
  as?: 'h1' | 'h2';
  /**
   * `center` - для экранов, где всё содержимое стоит узкой колонкой по центру: вход,
   * регистрация. Прижатый влево заголовок над центрованной формой висел в воздухе.
   * С `action` не сочетается: кнопке справа от центрованного заголовка некуда встать.
   */
  align?: 'start' | 'center';
};

/**
 * Заголовок экрана - один приём на все страницы.
 *
 * Писался четырьмя разными способами: `Text as="h1"`, `Text as="h2"`, `Heading` и просто
 * `Text` вовсе без тега. Последних было шесть из восемнадцати, и для читалки экрана они не
 * заголовки, а обычный текст: человек, который ходит по странице заголовками, эти экраны
 * пролистывал мимо.
 *
 * Не путать с `SectionHeading`: тот подписывает раздел ВНУТРИ страницы и живёт на кегле
 * `section`. Этот - сама страница, кегль `heading`, и на экране он один.
 */
export const PageHeading = ({
  children,
  description,
  action,
  as = 'h1',
  align = 'start',
}: PageHeadingProps) => (
  <HStack
    w="100%"
    gap={4}
    justifyContent={align === 'center' ? 'center' : 'space-between'}
    alignItems="flex-start"
  >
    <Stack gap={1} minW={0} textAlign={align === 'center' ? 'center' : undefined}>
      <Heading as={as} textStyle="heading">
        {children}
      </Heading>
      {description ? (
        <Text textStyle="body" color="fg.muted">
          {description}
        </Text>
      ) : null}
    </Stack>
    {action ? <HStack flexShrink={0}>{action}</HStack> : null}
  </HStack>
);
